import { IChecklistCategory, IChecklistItem, IChecklistItemRow, IChecklistPopulated } from "@mctypes/checklist.types";
import { IMinUserDepricated, IPNSProps } from "@mctypes/global.types";
import { createSelector } from "@reduxjs/toolkit";
import { GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg, openapi } from "../../../../openapi";
import { selectCurrentProjectId } from "../../../global/globalSliceCommon";
import { selectIsUserDev } from "../../../global/globalSlice";
import { IRootState } from "../../../store";
import { selectHttpProjectMembers, selectProjectPns } from "../../projectSelectors";
import { sortArrayByNumber } from "../../utils";
import { ChecklistTableVariant } from "./checklistSlice";
import { getActiveChecklistCheckKeys, getChecklistTableRowsRefactored } from "./ChecklistRefactor/checklistTableRefactorUtils";

export const selectChecklistDepricated = (state: IRootState): IChecklistPopulated | null => state.checklistReducer.checklist;
export const selectChecklistCategoriesDepricated = (state: IRootState): IChecklistCategory[] =>
  state.checklistReducer.checklistCategories;
export const selectChecklistItemsDepricated = (state: IRootState): IChecklistItem[] => state.checklistReducer.checklistItems;
export const selectChecklistActiveTab = (state: IRootState): string => state.checklistReducer.activeTab;
export const selectChecklistHiddenColumns = (state: IRootState): string[] => state.checklistReducer.hiddenColumns;
export const selectChecklistTableVariant = (state: IRootState) => state.checklistReducer.checklistTableVariant;
export const selectRefactoredCollapsedCategoryIds = (state: IRootState): string[] =>
  state.checklistReducer.refactoredCollapsedCategoryIds;
export const selectRefactoredExpandedCategoryIds = (state: IRootState): string[] =>
  state.checklistReducer.refactoredExpandedCategoryIds;

const EMPTY_CATEGORIES: IChecklistCategory[] = [];
const EMPTY_ITEMS: IChecklistItem[] = [];
const EMPTY_ROWS: IChecklistItemRow[] = [];
const EMPTY_PNS_ITEMS: Array<{ comboLabel: string; _id: string; parentId: string; props: IPNSProps }> = [];

export const selectEffectiveChecklistTableVariant = createSelector(
  [selectChecklistTableVariant, selectIsUserDev],
  (variant, isUserDev): ChecklistTableVariant => (isUserDev === true ? variant : "legacy"),
);

const selectChecklistIdArg = (_state: IRootState, checklistId?: string | null): string | null => checklistId ?? null;

export const selectChecklistPartsArgs = createSelector(
  [selectCurrentProjectId, selectChecklistIdArg],
  (projectId, checklistId): GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg | null => {
    if (projectId == null || checklistId == null) {
      return null;
    }

    return {
      projectId,
      checklistId,
    };
  },
);

export const selectHttpChecklistParts = createSelector(
  [(state: IRootState) => state, selectChecklistPartsArgs],
  (state, args) => {
    if (args == null) {
      return null;
    }

    return openapi.endpoints.getProjectByProjectIdChecklistAndChecklistIdParts.select(args)(state).data ?? null;
  },
);

export const selectActiveChecklist = createSelector(
  [selectHttpChecklistParts],
  (parts): IChecklistPopulated | null => (parts?.checklist ?? null) as unknown as IChecklistPopulated | null,
);

export const selectChecklistCategories = createSelector(
  [selectHttpChecklistParts],
  (parts): IChecklistCategory[] => {
    if (parts == null) {
      return EMPTY_CATEGORIES;
    }

    const categories = (parts.categories ?? EMPTY_CATEGORIES) as unknown as IChecklistCategory[];

    return sortArrayByNumber(
      categories.map((category) => ({
        ...category,
        collapsed: category.collapsed ?? false,
      })),
      "categoryId",
    );
  },
);

export const selectChecklistItems = createSelector(
  [selectHttpChecklistParts],
  (parts): IChecklistItem[] => {
    if (parts == null) {
      return EMPTY_ITEMS;
    }

    return sortArrayByNumber((parts.items ?? EMPTY_ITEMS) as unknown as IChecklistItem[], "itemId");
  },
);

export const selectChecklistRowsForPage = createSelector(
  [selectActiveChecklist, selectChecklistCategories, selectChecklistItems],
  (checklist, categories, items): IChecklistItemRow[] =>
    checklist == null ? EMPTY_ROWS : getChecklistTableRowsRefactored(checklist, categories, items),
);

export const selectCollapsedChecklistCategoryIdsForPage = createSelector(
  [selectChecklistCategories, selectRefactoredCollapsedCategoryIds, selectRefactoredExpandedCategoryIds],
  (categories, collapsedCategoryIds, expandedCategoryIds): Set<string> => {
    const expanded = new Set(expandedCategoryIds);
    const collapsed = new Set(collapsedCategoryIds);

    categories.forEach((category) => {
      if (category.collapsed === true && expanded.has(category._id) === false) {
        collapsed.add(category._id);
      }
    });

    return collapsed;
  },
);

export const selectChecklistCategoryCollapseStateForPage = createSelector(
  [selectChecklistRowsForPage, selectCollapsedChecklistCategoryIdsForPage],
  (rows, collapsedCategoryIds) => {
    const categoryIds = rows.flatMap((row) => (row.originalItem == null ? [row._id] : []));
    const allCategoriesCollapsed =
      categoryIds.length > 0 && categoryIds.every((categoryId) => collapsedCategoryIds.has(categoryId));

    return {
      allCategoriesCollapsed,
      categoryIds,
      collapsedCategoryIds,
    };
  },
);

export const selectChecklistDisplayRowsForPage = createSelector(
  [selectChecklistRowsForPage, selectCollapsedChecklistCategoryIdsForPage],
  (rows, collapsedCategoryIds): IChecklistItemRow[] =>
    rows.filter((row) => row.originalItem == null || collapsedCategoryIds.has(row.originalCategory._id) === false),
);

export const selectActiveChecklistCheckKeysForPage = createSelector([selectActiveChecklist], (checklist): string[] =>
  getActiveChecklistCheckKeys(checklist),
);

export const selectChecklistProgressSegmentsForPage = createSelector([selectActiveChecklist], (checklist) => {
  const accepted = Math.max(0, Math.min(100, Number(checklist?.progress?.accepted ?? 0)));
  const rejected = Math.max(0, Math.min(100 - accepted, Number(checklist?.progress?.rejected ?? 0)));

  return {
    accepted,
    rejected,
  };
});

export const selectChecklistLogChecklistIdForPage = createSelector(
  [selectActiveChecklist],
  (checklist): number | null => checklist?.id ?? null,
);

export const selectChecklistSettingsMemberUsers = createSelector([selectHttpProjectMembers], (members): IMinUserDepricated[] =>
  members.flatMap((member) => {
    if (typeof member.user === "string" || member.user == null) {
      return [];
    }

    return [{ _id: member.user._id, name: member.user.name }];
  }),
);

export const selectChecklistSettingsUserComboboxData = createSelector([selectChecklistSettingsMemberUsers], (users) =>
  users.map((user) => ({
    value: user._id,
    label: user.name,
  })),
);

export const selectChecklistSettingsPnsItems = createSelector([selectProjectPns], (pnsItems) =>
  (pnsItems ?? EMPTY_PNS_ITEMS).map((pns) => ({
    ...pns,
    comboLabel: [pns.props.pnsId, pns.props.title]
      .map((part) => part?.trim())
      .filter((part): part is string => part != null && part.length > 0)
      .join(" - "),
  })),
);

export const selectChecklistSettingsPnsAutocompleteData = createSelector([selectChecklistSettingsPnsItems], (pnsItems) =>
  pnsItems.map((pns) => ({
    value: pns._id,
    label: pns.comboLabel,
  })),
);

export const selectChecklistSelectedPnsLabelForPage = createSelector(
  [selectChecklistSettingsPnsItems, selectActiveChecklist],
  (pnsItems, checklist): string => pnsItems.find((pns) => pns.props.pnsId === checklist?.pns)?.comboLabel ?? "",
);

export const selectLatestChecklistChangeLabel = createSelector([selectChecklistItemsDepricated], (items): string | null => {
  const latestChange = (items ?? []).reduce((latest, item) => {
    const candidates = [
      {
        updatedDate: item.inactive?.updatedDate,
        updatedBy: item.inactive?.updatedBy,
      },
      ...Object.values(item.checks ?? {}),
    ];

    candidates.forEach((candidate) => {
      if (candidate?.updatedDate == null) {
        return;
      }

      const candidateDate = new Date(candidate.updatedDate);
      if (Number.isNaN(candidateDate.getTime())) {
        return;
      }

      if (latest == null || candidateDate > latest.updatedDate) {
        latest = {
          updatedDate: candidateDate,
          updatedBy: candidate.updatedBy,
        };
      }
    });

    return latest;
  }, null as { updatedDate: Date; updatedBy?: string | IMinUserDepricated } | null);

  if (latestChange == null) {
    return null;
  }

  const formattedDate = latestChange.updatedDate.toLocaleString("no-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const updatedBy = typeof latestChange.updatedBy === "string" ? "" : latestChange.updatedBy?.name;

  return updatedBy ? `${formattedDate}` : formattedDate;
});
